package com.tixforgigs.common.ui.pages.template

import com.tixforgigs.apax.models.TemplateConfig
import com.tixforgigs.apax.models.TemplateConfigType
import com.tixforgigs.apax.models.TemplateCreateRequest
import com.tixforgigs.apax.models.TemplateListResponse
import com.tixforgigs.common.core.annotations.MissingTaramiIconKey
import com.tixforgigs.common.core.enums.View
import com.tixforgigs.common.core.extensions.RoutingUtil.AppScope
import com.tixforgigs.common.core.extensions.RoutingUtil.routing
import com.tixforgigs.common.core.extensions.navigateTo
import com.tixforgigs.common.core.interfaces.StorefrontPage
import com.tixforgigs.common.core.util.TrackerUtil.pageView
import com.tixforgigs.common.http.ApiDeletePath
import com.tixforgigs.common.http.ApiGetPath
import com.tixforgigs.common.http.ApiPostPath
import com.tixforgigs.common.http.HttpClient.apiV4Delete
import com.tixforgigs.common.http.HttpClient.apiV4Get
import com.tixforgigs.common.http.HttpClient.apiV4Post
import com.tixforgigs.common.ui.components.grid.gridContainer
import com.tixforgigs.common.ui.components.mandarin.mandarinDialog
import com.tixforgigs.common.ui.components.mandarin.mandarinFilledButton
import com.tixforgigs.common.ui.components.mandarin.mandarinTitle
import com.tixforgigs.common.ui.components.root.defaultTemplate
import com.tixforgigs.tarami.AraiMessage.buttonTemplateConfiguratorCreateNew
import com.tixforgigs.tarami.AraiMessage.dialogTemplateConfiguratorConfigDetailDeleteTemplateContent
import com.tixforgigs.tarami.AraiMessage.dialogTemplateConfiguratorConfigDetailDeleteTemplateTitle
import com.tixforgigs.tarami.AraiMessage.dialogTemplateConfiguratorConfigListCreateNewTitle
import com.tixforgigs.tarami.AraiMessage.inputTextTemplateNameLabel
import com.tixforgigs.tarami.AraiMessage.titleTemplateConfiguratorConfigList
import com.tixforgigs.tarami.GlobalMessage.buttonCancel
import com.tixforgigs.tarami.GlobalMessage.buttonDelete
import com.tixforgigs.tarami.GlobalMessage.buttonSave
import com.tixforgigs.tarami.GlobalMessage.textTemplateConfiguratorConfigListEmpty
import io.kvision.core.AlignItems
import io.kvision.core.JustifyContent
import io.kvision.event.clickFlow
import io.kvision.event.inputFlow
import io.kvision.html.h4
import io.kvision.material.ExperimentalMaterialApi
import io.kvision.material.divider.divider
import io.kvision.material.icon.icon
import io.kvision.material.icon.leadingIcon
import io.kvision.material.icon.trailingIcon
import io.kvision.material.iconbutton.iconButton
import io.kvision.material.list.ListItemType
import io.kvision.material.list.listItem
import io.kvision.material.textfield.outlinedTextField
import io.kvision.panel.SimplePanel
import io.kvision.panel.VPanel
import io.kvision.panel.hPanel
import io.kvision.state.bind
import io.kvision.utils.perc
import io.kvision.utils.px
import kotlinx.browser.window
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.launchIn
import kotlinx.coroutines.flow.onEach
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

@OptIn(ExperimentalMaterialApi::class)
class TemplateConfiguratorOverviewView : SimplePanel(className = "xm-page xm-page-configurator"), StorefrontPage {

  override val VIEW: View
    get() = View.TEMPLATE_CONFIGURATIONS

  val mTemplateConfigurations: MutableStateFlow<List<TemplateConfig>?> = MutableStateFlow(null)

  val mTemplateNameInput = MutableStateFlow("")

  init {
    AppScope.launch {
      apiV4Get<TemplateListResponse>(ApiGetPath.TemplateConfigs)?.let { response ->
        mTemplateConfigurations.update { response.templateConfigs }
      }
      pageView()
    }

    defaultTemplate {
      gridContainer().bind(mTemplateConfigurations) { templates ->
        gridItem(12) {
          mandarinTitle(
            title = titleTemplateConfiguratorConfigList()
          )
        }
        templates?.forEachIndexed { index, template ->
          template.configItems.forEach { config ->
            gridItem(12) {
              hPanel(
                justify = JustifyContent.START,
                alignItems = AlignItems.CENTER
              ) {
                if (config.configType != TemplateConfigType.CURRENT) {
                  marginLeft = 32.px
                }
                listItem(
                  text = buildString {
                    append(config.templateName)
                    append(" (${config.configType.value})")
                  },
                  type = ListItemType.Link
                ) {
                  width = 100.perc
                  @MissingTaramiIconKey
                  leadingIcon("docs")
                  @MissingTaramiIconKey
                  trailingIcon("chevron_right")
                }.clickFlow.onEach {
                  template.templateId?.let { templateId ->
                    routing.navigateTo(
                      View.TEMPLATE_CONFIGURATION(
                        templateId.toString()
                      )
                    )
                  }
                }.launchIn(MainScope())

                config.configId?.let { configId ->
                  iconButton(className = "xm-icon-button") {
                    @MissingTaramiIconKey
                    icon("delete")
                    setAttribute("aria-label", "delete template")
                  }.clickFlow.onEach {
                    mandarinDialog(
                      title = dialogTemplateConfiguratorConfigDetailDeleteTemplateTitle(),
                      content = VPanel {
                        h4 {
                          +dialogTemplateConfiguratorConfigDetailDeleteTemplateContent(
                            configId,
                            config.templateName
                          )
                        }
                      },
                      cancel = buttonCancel() to { },
                      confirm = buttonDelete() to {
                        apiV4Delete<Unit>(
                          type = ApiDeletePath.TemplateConfigDelete(configId)
                        )?.let {
                          window.location.reload()
                        }
                      }
                    ).apply { open() }
                  }.launchIn(MainScope())
                }
              }
            }
          }
          if (index < templates.lastIndex) {
            gridItem(12) {
              divider()
            }
          }
        }
        if (templates.isNullOrEmpty()) {
          gridItem(12) {
            listItem(
              text = textTemplateConfiguratorConfigListEmpty()
            ) {
              @MissingTaramiIconKey
              leadingIcon("info")
            }
          }
        }
        gridItem(12) {
          mandarinFilledButton(
            title = buttonTemplateConfiguratorCreateNew()
          ) {
            mandarinDialog(
              title = dialogTemplateConfiguratorConfigListCreateNewTitle(),
              content = VPanel {
                outlinedTextField(
                  label = inputTextTemplateNameLabel(),
                ) {
                  width = 100.perc
                  marginTop = 8.px
                }.bind(mTemplateNameInput) { input ->
                  value = input
                }.inputFlow.onEach { input ->
                  mTemplateNameInput.update { input.value }
                }.launchIn(MainScope())
              },
              cancel = buttonCancel() to { },
              confirm = buttonSave() to {
                apiV4Post<TemplateConfig>(
                  type = ApiPostPath.TemplateConfigCreate(
                    body = TemplateCreateRequest(
                      templateName = mTemplateNameInput.value
                    )
                  )
                )?.let { config ->
                  mTemplateConfigurations.update { configs ->
                    configs.orEmpty().plus(config)
                  }
                }
              }
            ).apply { open() }
          }
        }
      }
    }
  }
}